// Nurse "Place Order" modal — the doctor console's structure-driven order form,
// minus the pharmacy store picker.
//
// Replaces the hand-built PlaceOrderModal. The form itself is
// NurseOrderActivityModal (a copy of the doctor's CarePlanActivityModal, kept in
// pages/ipd/components/modals so its ~56 relative imports keep resolving). The
// doctor's own modal is NOT touched or shared — a copy was taken deliberately so
// no doctor flow can regress.
//
// This wrapper owns the two things the form cannot: WHICH care plan is being
// filed (changing it swaps structureDef, which the form takes as a prop) and
// WHICH doctor the order is placed on behalf of.
//
// SUBMIT: everything goes through submit_care_plan/, which is the nurse-callable
// path — care_plans/ is doctor-gated ("Caller is not a doctor"). That one
// endpoint covers every template: it routes medication entries through
// create_medication_order and bills accepted test entries server-side.
//
// SUBMIT RESPONSE: submit_care_plan/ returns a 200 envelope that is "Success"
// even when an entry is rejected — the rejection lands in `skipped` (e.g. the
// actor is not permitted for that care plan). We treat the submit as placed ONLY
// when a care_plan_data_id came back AND nothing was skipped; any skip is
// surfaced as an error. This avoids the false-success bug class.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";

import NurseOrderActivityModal from "../../ipd/components/modals/NurseOrderActivityModal";
import {
  getCarePlansWithFulfillment, getAllDoctors, submitCarePlan, listOf,
} from "../api/nurseApi";

// Only medication and investigations are in scope for nurse-placed orders.
// Filtered by template_id, not by name — names are per-tenant data.
//   3 = Medication (multi_entry: several drugs queued in one save)
//   6 = Test       (its `category` radio is laboratory | radiology | other,
//                   so lab AND radiology both live here — neither needs the
//                   Doctor-only "Orders" care plan)
// The server still gates what a nurse MAY file (allowed_actors); this narrows
// what we OFFER. A plan the server withholds never reaches the picker.
const IN_SCOPE_TEMPLATE_IDS = [3, 6];

// Template 6 is named "Test" server-side, which undersells it — the same form
// files radiology. Say so in the picker rather than renaming tenant data.
const LABEL_SUFFIX = { 6: " (Lab / Radiology)" };

export default function NurseOrderModal({
  open,
  onClose,
  admissionId,
  careContextId = "",
  onPlaced,
  defaultDoctorId = "",
  defaultDoctorName = "",
}) {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [orderingDoctorId, setOrderingDoctorId] = useState(defaultDoctorId);

  // What the server permits this nurse (allowed_actors), narrowed to the order
  // types in scope. Both filters matter: the server decides what is ALLOWED,
  // IN_SCOPE_TEMPLATE_IDS decides what is OFFERED. Medication sorts first.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setPlansLoading(true);
    getCarePlansWithFulfillment("nurse")
      .then((res) => {
        if (!alive) return;
        const list = listOf(res)
          .filter((p) => p?.id && IN_SCOPE_TEMPLATE_IDS.includes(Number(p.template_id)))
          .sort((a, b) =>
            IN_SCOPE_TEMPLATE_IDS.indexOf(Number(a.template_id)) -
            IN_SCOPE_TEMPLATE_IDS.indexOf(Number(b.template_id)));
        setPlans(list);
        setSelectedPlanId((cur) => cur || String(list[0]?.id || ""));
      })
      .catch(() => { if (alive) setPlans([]); })
      .finally(() => { if (alive) setPlansLoading(false); });
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    getAllDoctors()
      .then((res) => { if (alive) setDoctors(listOf(res).filter((d) => d?.doctor_id)); })
      .catch(() => { if (alive) setDoctors([]); });
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (open) setOrderingDoctorId(defaultDoctorId);
  }, [open, defaultDoctorId]);

  // Keep the defaulted treating doctor selectable even when the facility list
  // hasn't loaded or doesn't contain them.
  const doctorOptions = useMemo(() => {
    if (defaultDoctorId && !doctors.some((d) => d.doctor_id === defaultDoctorId)) {
      return [{ doctor_id: defaultDoctorId, name: defaultDoctorName || defaultDoctorId }, ...doctors];
    }
    return doctors;
  }, [doctors, defaultDoctorId, defaultDoctorName]);

  const selectedPlan = plans.find((p) => String(p.id) === String(selectedPlanId)) || null;
  const orderingDoctorName =
    doctorOptions.find((d) => d.doctor_id === orderingDoctorId)?.name || "";

  // The form hands back either one entry or an array of them (multi-entry care
  // plans). Returning false tells it the save failed, so it keeps the dialog
  // open with the nurse's work intact.
  const handleSubmit = useCallback(async (payload) => {
    if (!selectedPlan) return false;
    const entries = Array.isArray(payload) ? payload : [payload];
    try {
      const res = await submitCarePlan(
        admissionId,
        entries.map((data) => ({ care_plan_id: selectedPlan.id, data })),
      );
      const ids = Array.isArray(res?.care_plan_data_ids) ? res.care_plan_data_ids : [];
      const skipped = Array.isArray(res?.skipped) ? res.skipped : [];
      if (res?.status !== "Success" || !ids.length || skipped.length) {
        toast.error(skipped[0]?.reason || res?.message || "Could not place this order.");
        return false;
      }
      toast.success("Order placed.");
      onPlaced?.();
      onClose?.();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not place this order.");
      return false;
    }
  }, [admissionId, selectedPlan, onPlaced, onClose]);

  const header = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
      <TextField
        select
        size="small"
        label="Order type"
        value={selectedPlanId}
        onChange={(e) => setSelectedPlanId(e.target.value)}
        disabled={plansLoading}
        fullWidth
      >
        {plans.map((p) => (
          <MenuItem key={p.id} value={String(p.id)}>
            {p.name}{LABEL_SUFFIX[Number(p.template_id)] || ""}
          </MenuItem>
        ))}
      </TextField>
      <Box>
        <TextField
          select
          size="small"
          label="Ordering doctor *"
          value={orderingDoctorId}
          onChange={(e) => setOrderingDoctorId(e.target.value)}
          fullWidth
        >
          {doctorOptions.map((d) => (
            <MenuItem key={d.doctor_id} value={d.doctor_id}>{d.name || d.doctor_id}</MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          This order is placed on behalf of the selected doctor — required for medication.
        </Typography>
      </Box>
    </Box>
  );

  if (!open || !selectedPlan) return null;

  return (
    <NurseOrderActivityModal
      open={open}
      onClose={onClose}
      title="Place Order"
      headerSlot={header}
      structureDef={selectedPlan}
      onSubmit={handleSubmit}
      admissionId={admissionId}
      careContextId={careContextId}
      orderingDoctorId={orderingDoctorId}
      orderingDoctorName={orderingDoctorName}
    />
  );
}
